import { useEffect, useId, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useSecureMacProAccount } from "@/context/SecureMacProAccountContext";
import DeviceApprovalCard from "./DeviceApprovalCard";

export default function ProductDeviceActivation({ product, productName, devices = [], devicesError }) {
  const { refresh, owned, secureShieldEntitlement, secureAIEntitlement } = useSecureMacProAccount();
  const location = useLocation();
  const [params] = useSearchParams();
  const eligible = [
    owned && "securemac-pro",
    secureShieldEntitlement?.active === true && secureShieldEntitlement?.status === "active" && "secureshield",
    secureAIEntitlement?.active === true && secureAIEntitlement?.status === "active" && "secureai",
  ].filter(Boolean);
  const requestedProduct = params.get("product");
  // Older apps supply only a code. Never guess the product when several are owned.
  const linked = location.pathname.replace(/\/+$/, "") === "/account/approve-device"
    && (requestedProduct === product || (!requestedProduct && eligible.length === 1 && eligible[0] === product));
  const [expanded, setExpanded] = useState(linked);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const regionId = useId();
  const region = useRef(null);
  const open = expanded;
  const activeCount = devices.filter(device => !device.revoked).length;

  useEffect(() => {
    if (linked) {
      setExpanded(true);
      region.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [linked, location.search]);

  const refreshDevices = async () => {
    setRefreshing(true);
    setRefreshError(false);
    try { await refresh(); } catch { setRefreshError(true); }
    finally { setRefreshing(false); }
  };

  return (
    <section className="border-t border-ink-100 pt-5" ref={region} aria-label={`${productName} device activation`}>
      <h3 className="font-display text-lg font-bold">Device activation</h3>
      <p className="mt-2 text-sm text-ink-600">
        {devicesError ? "Device status is temporarily unavailable." : activeCount
          ? `${activeCount} ${activeCount === 1 ? "device is" : "devices are"} activated for ${productName}.`
          : `No devices are activated for ${productName} yet.`}
      </p>
      <p className="mt-2 text-xs text-ink-500">Your purchase and each device's activation are separate. Use the code shown in the app to approve a computer.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="lf-btn-secondary" aria-expanded={open} aria-controls={regionId}
          onClick={() => setExpanded(value => !value)}>
          {open ? "Hide activation" : activeCount ? "Activate another device" : "Activate a device"}
        </button>
        <button type="button" className="lf-link" onClick={refreshDevices} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Refresh device status"}
        </button>
      </div>
      <div id={regionId} hidden={!open}>
        {open ? <div className="mt-4"><DeviceApprovalCard productName={productName} onApproved={refresh} /></div> : null}
      </div>
      {refreshError ? <p role="alert" className="mt-3 text-sm text-red-600">We couldn't refresh device status. Please try again.</p> : null}
    </section>
  );
}
